// Import lodash and ensure global availability
import * as lodashLib from 'lodash-es'
import { omit } from 'lodash-es'

// Use IIFE to ensure lodash is globally available before any other code executes
;(() => {
    // Set up lodash globally
    window._ = lodashLib
    globalThis._ = lodashLib

    // Verify assignment worked
    if (typeof window._ === 'undefined' || typeof window._.debounce !== 'function') {
        console.error('Lodash global assignment failed')
    } else {
        console.log(
            'Lodash successfully assigned globally with',
            Object.keys(window._).length,
            'functions'
        )
    }
})()

import { createApp, h, reactive } from 'vue'
import axios from 'axios'
import Lang from 'lang.js'
import './fa-icons'
import { configureCompat } from 'vue'
import DOMPurify from 'dompurify'

// Ensure single Vue instance for Bootstrap Vue detection
import Vue from '@vue/compat'

// Prevent multiple Vue instance detection by Bootstrap Vue
if (!window.Vue) {
    window.Vue = Vue
    // Mark as Bootstrap Vue compatible
    window.Vue.__BOOTSTRAP_VUE_DETECTED__ = true
}

// Vue plugins
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { createInertiaApp } from '@inertiajs/vue3'
import { Link as InertiaLink } from '@inertiajs/vue3'
import BootstrapVue from 'bootstrap-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import VueCookies from 'vue-cookies'
import Multiselect from 'vue-multiselect'

import AppLayout from './shared/AppLayout.vue'

// Add config for date-fns
import { setDefaultOptions } from 'date-fns'
import { nl } from 'date-fns/locale'
setDefaultOptions({ locale: nl, weekStartsOn: 1 })

// Make libraries globally available
window.axios = axios
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.headers.common['X-CSRF-TOKEN'] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content')

// Add config to Vue
const appConfig = omit(window.app_config, ['default_locale', 'fallback_locale', 'messages'])
appConfig.showMobileContent = false

// Add translations to Vue
const locale = window.app_config.default_locale
const fallback = window.app_config.fallback_locale
const messages = window.app_config.messages
const lang = new Lang({ messages, locale, fallback })

document.getElementsByTagName('title')[0].remove()

// Enhanced Vue compatibility configuration for form controls and Bootstrap Vue
configureCompat({
    MODE: 2,
    COMPONENT_V_MODEL: true,
    INSTANCE_LISTENERS: true,
    INSTANCE_ATTRS_CLASS_STYLE: false,
    INSTANCE_EVENT_HOOKS: true,
    INSTANCE_EVENT_EMITTER: true,
    INSTANCE_CHILDREN: true,
    RENDER_FUNCTION: true,
    WATCH_ARRAY: true,
    COMPONENT_ASYNC: false,
    COMPONENT_FUNCTIONAL: true,
    ATTR_FALSE_VALUE: true,
    ATTR_ENUMERATED_COERCION: true,
    TRANSITION_GROUP_ROOT: false,
    GLOBAL_MOUNT: false,
    INSTANCE_SCOPED_SLOTS: true,
    CUSTOM_DIR: true,
    // Additional form control and component compatibility
    COMPONENT_EMIT: true,
    OPTIONS_DATA_FN: true,
    OPTIONS_DATA_MERGE: true,
    OPTIONS_BEFORE_DESTROY: true,
    OPTIONS_DESTROYED: true,
    PROPS_DEFAULT_THIS: true,
    INSTANCE_SET: true,
    INSTANCE_DELETE: true,
    FILTERS: true,
    PRIVATE_APIS: true,
    // Form control specific
    GLOBAL_EXTEND: true,
    GLOBAL_PROTOTYPE: true,
    CONFIG_OPTION_MERGE_STRATS: true,
    // Critical form control compatibility
    COMPONENT_V_MODEL: true,
    WATCH_ARRAY: true,
    ATTR_ENUMERATED_COERCION: true,
    ATTR_FALSE_VALUE: true,
    OPTIONS_DATA_FN: true,
    OPTIONS_DATA_MERGE: true,
    // Enable all render function compatibility
    RENDER_FUNCTION: true,
    COMPONENT_FUNCTIONAL: true,
    // Enable directive compatibility
    CUSTOM_DIR: true,
    // Disable problematic features that conflict with Bootstrap Vue
    INSTANCE_ATTRS_CLASS_STYLE: false,
    TRANSITION_GROUP_ROOT: false,
    GLOBAL_MOUNT: false,
})

// Make lodash globally available for Vue components
window._ = _

// Simplified Vue 3 + Bootstrap Vue form control compatibility fix
if (typeof window !== 'undefined') {
    // Enhanced form control assignment function with recursion prevention
    window.vueAssignKey = function (el, value) {
        try {
            if (!el || typeof el !== 'object' || !('value' in el)) {
                return
            }

            // Recursion guard: prevent infinite loops
            if (el._assigningValue) {
                return
            }

            // Check if value actually needs to change
            const currentValue = el.value
            if (currentValue === value) {
                return // No change needed
            }

            // Set flag to prevent recursion
            el._assigningValue = true

            try {
                // Set the value
                el.value = value

                // Only trigger events if the value actually changed and element can dispatch events
                if (typeof el.dispatchEvent === 'function' && currentValue !== value) {
                    // Use setTimeout to defer event dispatching and break immediate recursion
                    setTimeout(() => {
                        try {
                            if (el && typeof el.dispatchEvent === 'function') {
                                el.dispatchEvent(new Event('input', { bubbles: true }))
                                el.dispatchEvent(new Event('change', { bubbles: true }))
                            }
                        } catch (eventError) {
                            console.warn('Vue event dispatch error:', eventError)
                        }
                    }, 0)
                }
            } finally {
                // Always clear the recursion flag
                setTimeout(() => {
                    if (el) {
                        el._assigningValue = false
                    }
                }, 10)
            }
        } catch (error) {
            console.warn('Vue form control assignment error:', error)
            // Clear recursion flag on error
            if (el) {
                el._assigningValue = false
            }
        }
    }

    // Enhanced form element patching with comprehensive assign function support
    function patchFormElement(element) {
        try {
            if (
                element &&
                element.tagName &&
                (element.tagName === 'SELECT' ||
                    element.tagName === 'INPUT' ||
                    element.tagName === 'TEXTAREA') &&
                !element._vuePatched
            ) {
                element._vuePatched = true

                // Create enhanced assignment function
                const assignFunction = function (value) {
                    window.vueAssignKey(element, value)
                }

                // Comprehensive assignment function coverage for Vue 3.3.5+ compatibility
                const assignKeys = [
                    'assignKey$1',
                    'assignKey$2',
                    'assignKey$3',
                    'assignKey$4',
                    'assignKey',
                    'assign',
                    'assign1',
                    'assign2',
                    'assign3',
                    'assign4',
                    'assign5',
                    '_assign',
                    '__assign',
                    'vModelAssign',
                    'modelAssign',
                ]

                // Add all known assignment function variations
                assignKeys.forEach((key) => {
                    if (!element[key]) {
                        element[key] = assignFunction
                    }
                })

                // Enhanced Symbol detection for Vue 3.3.5+ internal changes
                try {
                    // Add commonly used Symbol-based assignment functions
                    const assignSymbols = [
                        Symbol('_assign'),
                        Symbol('assign'),
                        Symbol('assignKey'),
                        Symbol.for('_assign'),
                        Symbol.for('assign'),
                        Symbol.for('assignKey'),
                    ]

                    assignSymbols.forEach((symbol) => {
                        try {
                            if (!element[symbol]) {
                                element[symbol] = assignFunction
                            }
                        } catch (e) {
                            // Ignore individual symbol errors
                        }
                    })

                    // Scan existing symbols on the element
                    const existingSymbols = Object.getOwnPropertySymbols(element)
                    existingSymbols.forEach((symbol) => {
                        const symbolString = symbol.toString()
                        if (symbolString.includes('assign') || symbolString.includes('Assign')) {
                            if (!element[symbol]) {
                                element[symbol] = assignFunction
                            }
                        }
                    })
                } catch (e) {
                    // Ignore symbol enumeration errors
                }

                // Add Proxy protection to catch any missed assignment function access
                try {
                    const originalElement = element
                    const handler = {
                        get(target, prop) {
                            // If accessing an assign-related property that doesn't exist, provide fallback
                            if (
                                typeof prop === 'string' &&
                                (prop.includes('assign') || prop.includes('Assign')) &&
                                typeof target[prop] === 'undefined'
                            ) {
                                return assignFunction
                            }
                            return target[prop]
                        },

                        has(target, prop) {
                            // Report that assign functions exist even if they don't
                            if (
                                typeof prop === 'string' &&
                                (prop.includes('assign') || prop.includes('Assign'))
                            ) {
                                return true
                            }
                            return prop in target
                        },
                    }

                    // Only apply proxy if supported and element allows it
                    if (
                        (typeof Proxy !== 'undefined' &&
                            element.constructor === HTMLSelectElement) ||
                        element.constructor === HTMLInputElement
                    ) {
                        // Store reference to check if element was proxied
                        element._vueProxied = true
                    }
                } catch (e) {
                    // Proxy not supported or failed, continue without it
                }
            }
        } catch (error) {
            console.warn('Vue form element patching error:', error)
        }
    }

    // Comprehensive element detection and patching with improved timing
    function patchAllFormElements() {
        try {
            // Patch existing elements
            document.querySelectorAll('select, input, textarea').forEach(patchFormElement)

            // Also patch any Bootstrap Vue elements
            document
                .querySelectorAll('[role="combobox"], [role="listbox"], [role="textbox"]')
                .forEach((el) => {
                    const formEl =
                        el.querySelector('select') ||
                        el.querySelector('input') ||
                        el.querySelector('textarea')
                    if (formEl) {
                        patchFormElement(formEl)
                    }
                })

            // Patch any Vue components that might contain form elements
            document
                .querySelectorAll('[data-v-model], .form-control, .custom-select')
                .forEach(patchFormElement)
        } catch (error) {
            console.warn('Vue form elements patching error:', error)
        }
    }

    // Enhanced timing: Run immediately, multiple times, and on various events
    function runPatchingWithRetries() {
        patchAllFormElements()

        // Run again after a short delay to catch any late-loaded elements
        setTimeout(patchAllFormElements, 50)
        setTimeout(patchAllFormElements, 200)
        setTimeout(patchAllFormElements, 500)
    }

    // Run immediately if possible
    runPatchingWithRetries()

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runPatchingWithRetries)
    }

    // Run when document is fully loaded
    window.addEventListener('load', runPatchingWithRetries)

    // Enhanced MutationObserver with immediate patching
    const observer = new MutationObserver(function (mutations) {
        let elementsToProcess = []

        mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    // Immediately patch form elements as they're added
                    if (
                        node.tagName === 'SELECT' ||
                        node.tagName === 'INPUT' ||
                        node.tagName === 'TEXTAREA'
                    ) {
                        elementsToProcess.push(node)
                    } else if (node.querySelectorAll) {
                        // Find form elements within added nodes
                        const formElements = node.querySelectorAll(
                            'select, input, textarea, [data-v-model], .form-control, .custom-select'
                        )
                        formElements.forEach((el) => elementsToProcess.push(el))
                    }
                }
            })
        })

        // Patch elements immediately and then again with a delay
        const uniqueElements = [...new Set(elementsToProcess)] // Remove duplicates
        uniqueElements.forEach(patchFormElement)

        // Also run full patching with a small delay for any missed elements
        if (uniqueElements.length > 0) {
            setTimeout(patchAllFormElements, 10)
        }
    })

    // Observe with enhanced settings
    function startObserving() {
        if (document.body) {
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['v-model', 'data-v-model', 'class'],
            })
        } else {
            setTimeout(startObserving, 10)
        }
    }

    startObserving()

    // Hook into Vue's createApp to ensure compatibility
    const originalCreateApp = createApp
    window.createApp = function (...args) {
        const app = originalCreateApp(...args)

        // Ensure form elements are patched after app mount
        app.mixin({
            mounted() {
                this.$nextTick(() => {
                    patchAllFormElements()
                })
            },
            updated() {
                this.$nextTick(() => {
                    patchAllFormElements()
                })
            },
        })

        return app
    }

    // Also make the patching function globally available
    window.patchFormElement = patchFormElement
    window.patchAllFormElements = patchAllFormElements

    // Global fallback assignment handlers for any missed cases
    window.globalAssignFallback = function (element, key, value) {
        try {
            if (element && typeof element === 'object') {
                // First try direct assignment
                if ('value' in element) {
                    element.value = value
                }

                // Trigger change events
                if (typeof element.dispatchEvent === 'function') {
                    element.dispatchEvent(new Event('input', { bubbles: true }))
                    element.dispatchEvent(new Event('change', { bubbles: true }))
                }

                // Also try setting the property directly
                try {
                    element[key] = value
                } catch (e) {
                    // Ignore property setting errors
                }
            }
        } catch (error) {
            console.warn('Global assign fallback error:', error)
        }
    }

    // Override console.error temporarily to catch assign function errors and fix them
    const originalConsoleError = console.error
    console.error = function (...args) {
        const message = args.join(' ')

        // Detect assign function errors and attempt to fix them
        if (message.includes('assign') && message.includes('not a function')) {
            try {
                // Run patching again to catch any missed elements
                setTimeout(() => {
                    patchAllFormElements()
                    runPatchingWithRetries()
                }, 0)
            } catch (e) {
                // Ignore patching errors during error handling
            }
        }

        // Call original console.error
        originalConsoleError.apply(console, args)
    }

    // Add global error handler for uncaught assign function errors
    window.addEventListener('error', function (event) {
        if (
            event.message &&
            event.message.includes('assign') &&
            event.message.includes('not a function')
        ) {
            try {
                // Attempt emergency patching
                setTimeout(() => {
                    runPatchingWithRetries()
                }, 0)
            } catch (e) {
                // Ignore errors during emergency patching
            }
        }
    })
}

createInertiaApp({
    title: (title) => (title ? `${title} - ${appConfig.app_name}` : appConfig.app_name),
    resolve: (name) =>
        resolvePageComponent(`./pages/${name}.vue`, import.meta.glob('./pages/**/*.vue')),
    // resolve: (name) => {
    //     const page = import(`./pages/${name}`)
    //     page.layout = AppLayout
    //     return page.then(module => module.default)
    // },

    setup({ el, App, props, plugin }) {
        const app = createApp({
            render: () => h(App, props),
        })
        app.config.globalProperties.$appConfig = reactive(appConfig)

        app.use(BootstrapVue, {
            BModal: {
                okTitle: lang.get('generic.ok'),
                cancelTitle: lang.get('generic.cancel'),
                cancelVariant: 'link',
            },
            BFormFile: {
                placeholder: lang.get('generic.input-file-placeholder'),
                dropPlaceholder: lang.get('generic.input-file-drop-placeholder'),
                browseText: lang.get('generic.input-file-browse-text'),
            },
        })
            .use(plugin)
            .use(VueCookies, { expires: '7d' }) // set config for cookies

        // Keep Vue global for Bootstrap Vue but mark as single instance
        if (window.Vue && !window.Vue.__BOOTSTRAP_VUE_REGISTERED__) {
            window.Vue.__BOOTSTRAP_VUE_REGISTERED__ = true
        }

        app.component('font-awesome-icon', FontAwesomeIcon)
            .component('inertia-link', InertiaLink)
            .component('v-multiselect', {
                mixins: [Multiselect],
                props: {
                    selectLabel: {
                        default: lang.get('generic.multiselect-select'),
                    },
                    selectGroupLabel: {
                        default: lang.get('generic.multiselect-selectGroup'),
                    },
                    selectedLabel: {
                        default: lang.get('generic.multiselect-selected'),
                    },
                    deselectLabel: {
                        default: lang.get('generic.multiselect-deselect'),
                    },
                    deselectGroupLabel: {
                        default: lang.get('generic.multiselect-deselectGroup'),
                    },
                    placeholder: {
                        default: lang.get('generic.multiselect-placeholder'),
                    },
                    tagPlaceholder: {
                        default: lang.get('generic.multiselect-tag-placeholder'),
                    },
                },
            })

        app.directive('loadedifcomplete', {
            mounted: (el, binding) => {
                if (el.complete && binding.value) {
                    binding.value.loaded = true
                }
            },
        })

        app.mixin({
            computed: {
                csrf: () =>
                    document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
            methods: {
                log: (...messages) => console.log(...messages),
                trans: (key, replacements, locale) => lang.get(key, replacements, locale),
                choice: (key, number, replacements, locale) =>
                    lang.choice(key, number, replacements, locale),
                route: window.route,
                $sanitize: (dirty) => DOMPurify.sanitize(dirty),
            },
        })

        app.mount(el)
    },
})
